import { calendarFromApi, calendarToday } from '@/lib/calendarDate'

export type DocumentPayStatus = 'paid' | 'notPaid' | 'overdue'

type TranslateFn = (key: string, options?: Record<string, unknown>) => string

export function getDocumentPayStatus(input: {
  payStatus?: string | null
  remainingAmount?: number | null
  paidAmount?: number | null
  total?: number | null
  dueDate?: string | Date | null
}): DocumentPayStatus {
  if (isDocumentPaid(input)) return 'paid'

  const dueDay = calendarFromApi(input.dueDate)
  if (dueDay && dueDay < calendarToday()) return 'overdue'

  return 'notPaid'
}

function daysInMonth(year: number, month1to12: number): number {
  return new Date(year, month1to12, 0).getDate()
}

function calendarAge(fromDay: string, toDay: string): { years: number; months: number; days: number } {
  const [y1, m1, d1] = fromDay.split('-').map(Number)
  const [y2, m2, d2] = toDay.split('-').map(Number)

  let years = y2 - y1
  let months = m2 - m1
  let days = d2 - d1

  if (days < 0) {
    months -= 1
    let borrowYear = y2
    let borrowMonth = m2 - 1
    if (borrowMonth < 1) {
      borrowMonth += 12
      borrowYear -= 1
    }
    days += daysInMonth(borrowYear, borrowMonth)
    while (days < 0) {
      months -= 1
      borrowMonth -= 1
      if (borrowMonth < 1) {
        borrowMonth += 12
        borrowYear -= 1
      }
      days += daysInMonth(borrowYear, borrowMonth)
    }
  }

  if (months < 0) {
    years -= 1
    months += 12
  }

  if (years < 0) return { years: 0, months: 0, days: 0 }
  return { years, months, days }
}

export function formatOverdueDuration(
  dueDate: string | Date | null | undefined,
  t: TranslateFn,
): string {
  const dueDay = calendarFromApi(dueDate)
  const today = calendarToday()
  if (!dueDay || dueDay >= today) return ''

  const { years, months, days } = calendarAge(dueDay, today)
  const parts: string[] = []

  if (years > 0) {
    parts.push(t(years === 1 ? 'common.durationYear' : 'common.durationYears', { count: years }))
  }
  if (months > 0) {
    parts.push(t(months === 1 ? 'common.durationMonth' : 'common.durationMonths', { count: months }))
  }
  if (days > 0 || parts.length === 0) {
    parts.push(t(days === 1 ? 'common.durationDay' : 'common.durationDays', { count: days }))
  }

  return parts.join(' ')
}

export function formatOverduePayStatusLabel(
  dueDate: string | Date | null | undefined,
  t: TranslateFn,
  overdueKey: string,
  overdueWithDurationKey: string,
): string {
  const duration = formatOverdueDuration(dueDate, t)
  if (!duration) return t(overdueKey)
  return t(overdueWithDurationKey, { duration })
}

function isDocumentPaid(input: {
  payStatus?: string | null
  remainingAmount?: number | null
  paidAmount?: number | null
  total?: number | null
}): boolean {
  const status = String(input.payStatus ?? '').trim().toLowerCase()
  if (status === 'paid' || status === 'settled') return true

  const remaining = Number(input.remainingAmount)
  const paid = Number(input.paidAmount)
  const total = Number(input.total)

  if (Number.isFinite(remaining) && remaining <= 0) {
    if (Number.isFinite(total) && total > 0) return true
    if (Number.isFinite(paid) && paid > 0) return true
  }

  if (Number.isFinite(paid) && Number.isFinite(total) && total > 0 && paid >= total) return true

  return false
}
